import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ACTIVITY_TYPE_LABELS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { TimelineItem } from '@/api/types';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { Button } from '@/components/ui/Button';
import { TraceLink } from '@/features/traces/TraceLink';
import { formatTimelineItem, groupTimelineItems } from './formatTimelineItem';
import styles from './Activities.module.css';
import leadStyles from '@/features/leads/Leads.module.css';

const FILTERS = [
  { id: '', label: 'Alles' },
  { id: 'note', label: 'Notities' },
  { id: 'task', label: 'Taken' },
  { id: 'call', label: 'Calls' },
  { id: 'mail', label: 'Mail' },
  { id: 'system', label: 'Systeem' },
] as const;

type Props = {
  projectId: string;
  leadId?: string;
  dealId?: string;
  accountId?: string;
  limit?: number;
};

export function ActivityTimeline({ projectId, leadId, dealId, accountId, limit }: Props) {
  const [typeFilter, setTypeFilter] = useState('');

  const params = new URLSearchParams({ project_id: projectId });
  if (leadId) params.set('lead_id', leadId);
  if (dealId) params.set('deal_id', dealId);
  if (accountId) params.set('account_id', accountId);
  if (typeFilter) params.set('types', typeFilter);
  if (limit) params.set('limit', String(limit));

  const timelineQ = useQuery({
    queryKey: ['timeline', params.toString()],
    queryFn: () =>
      apiFetch<{ pinned: TimelineItem[]; items: TimelineItem[] }>(`/api/timeline?${params}`),
  });

  const groups = useMemo(
    () => groupTimelineItems(timelineQ.data?.items ?? []),
    [timelineQ.data?.items],
  );

  if (timelineQ.isLoading) return <LoadingState label="Timeline laden…" />;

  const compact = Boolean(limit);

  return (
    <div>
      {!compact ? (
      <div className={styles.filterRow}>
        {FILTERS.map((f) => (
          <Button
            key={f.id || 'all'}
            type="button"
            variant={typeFilter === f.id ? 'primary' : 'ghost'}
            onClick={() => setTypeFilter(f.id)}
          >
            {f.label}
          </Button>
        ))}
      </div>
      ) : null}

      {(timelineQ.data?.pinned ?? []).length ? (
        <div className={styles.pinned}>
          <strong>Vastgepind</strong>
          <ul className={leadStyles.timeline}>
            {timelineQ.data!.pinned.map((item) => (
              <TimelineRow key={item.id} item={item} />
            ))}
          </ul>
        </div>
      ) : null}

      {groups.map((g) => (
        <div key={g.label}>
          <div className={styles.dayHeader}>{g.label}</div>
          <ul className={leadStyles.timeline}>
            {g.items.map((item) => (
              <TimelineRow key={item.id} item={item} />
            ))}
          </ul>
        </div>
      ))}

      {!timelineQ.data?.items.length && !timelineQ.data?.pinned.length ? (
        <p className={leadStyles.muted}>Nog geen activiteiten.</p>
      ) : null}
    </div>
  );
}

function TimelineRow({ item }: { item: TimelineItem }) {
  const kindLabel =
    item.source === 'activity'
      ? (ACTIVITY_TYPE_LABELS[item.kind as keyof typeof ACTIVITY_TYPE_LABELS] ?? item.kind)
      : item.kind.replace(/_/g, ' ');

  return (
    <li className={styles.timelineItem}>
      <div className={styles.timelineKind}>{kindLabel}</div>
      <div>{formatTimelineItem(item)}</div>
      {item.internal_only === false ? (
        <span className={styles.internalBadge}> zichtbaar voor klant</span>
      ) : null}
      <small>{new Date(item.created_at).toLocaleString('nl-NL')}</small>
      {typeof item.payload.trace_id === 'string' ? (
        <div>
          <TraceLink traceId={item.payload.trace_id} />
        </div>
      ) : null}
    </li>
  );
}
